import React from 'react';
import { cn } from '../../utils/cn';
import type { InputProps } from '../../types/components';

const inputSizes = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-3 py-2 text-base',
  lg: 'px-4 py-3 text-lg',
  xl: 'px-5 py-4 text-xl',
};

const inputStatus: Record<string, string> = {
  default: 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
  success: 'border-success-300 focus:border-success-500 focus:ring-success-500',
  warning: 'border-warning-300 focus:border-warning-500 focus:ring-warning-500',
  error: 'border-error-300 focus:border-error-500 focus:ring-error-500',
  info: 'border-info-300 focus:border-info-500 focus:ring-info-500',
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      size = 'md',
      status = 'default',
      disabled = false,
      readOnly = false,
      leftIcon,
      rightIcon,
      className,
      testId,
      ...props
    },
    ref
  ) => {
    const baseClasses = [
      'block w-full',
      'border rounded-md',
      'transition-colors duration-200',
      'focus:outline-none focus:ring-1',
      'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
      'read-only:bg-gray-50 read-only:cursor-default',
    ];

    const sizeClasses = inputSizes[size] || inputSizes.md;
    const statusClasses = inputStatus[status] || inputStatus.default;

    const classes = cn(
      baseClasses,
      sizeClasses,
      statusClasses,
      {
        'pl-10': leftIcon,
        'pr-10': rightIcon,
      },
      className
    );

    if (leftIcon || rightIcon) {
      return (
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400">{leftIcon}</span>
            </div>
          )}
          <input
            ref={ref}
            type={type}
            disabled={disabled}
            readOnly={readOnly}
            className={classes}
            data-testid={testId}
            {...props}
          />
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span className="text-gray-400">{rightIcon}</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <input
        ref={ref}
        type={type}
        disabled={disabled}
        readOnly={readOnly}
        className={classes}
        data-testid={testId}
        {...props}
      />
    );
  }
);

Input.displayName = 'Input';